import { useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FileText, Sparkles, UploadCloud } from "lucide-react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select, Textarea } from "@/components/ui/Select";
import { ApiError } from "@/api/client";
import {
  knowledgeBaseApi,
  type KbAiIntakeBatch,
  type KbAiIntakeCommitRow,
  type KbAiIntakeRow,
  type KbAiIntakeStatus,
  type KbCategory,
  type KbConfidentiality,
  type KbDocument,
  type KbTag,
} from "@/api/knowledgeBase";
import { useToast } from "@/components/ui/Toast";

// Knowledge Base AI-Assisted Uploader v1.0 PR 2 — admin "AI Upload" dialog.
// Drop files → server classifies (synchronous, capped at 25) → editable
// review grid per row → commit applies edits to the staged Drafts. Sibling
// dialog of KbDocumentUploadDialog (kept separate to avoid layering a third
// branch into the existing single/bulk dialog).

const MAX_BATCH = 25;
const HIGH_CONFIDENCE = 0.75;
const SUPERSEDES_RECOMMENDED_THRESHOLD = 0.7;

interface KbAiIntakeDialogProps {
  categories: KbCategory[];
  tags: KbTag[];
  onClose: () => void;
  onSaved: () => void;
}

// Local editable state per row — seeded from KbAiIntakeRow, mutated as the
// admin reviews. `included` defaults to false for Failed/Duplicate rows
// (their Drafts either don't exist or won't be committable) and true
// otherwise.
interface EditableRow {
  suggestionId: string;
  documentId: string | null;
  fileName: string;
  status: KbAiIntakeStatus;
  errorMessage: string | null;
  confidence: number | null;
  categoryCode: string;
  tagCodes: string[];
  title: string;
  description: string;
  confidentiality: KbConfidentiality;
  registryAbbrev: string | null;
  documentVersion: string | null;
  supersedesCandidateDocumentId: string | null;
  supersedesCandidateTitle: string | null;
  supersedesConfidence: number | null;
  archivePredecessor: boolean;
  included: boolean;
}

function seedRow(row: KbAiIntakeRow): EditableRow {
  const supersedesRecommended =
    row.supersedesCandidateDocumentId != null &&
    (row.supersedesConfidence ?? 0) >= SUPERSEDES_RECOMMENDED_THRESHOLD;
  const committable = row.status !== "Failed" && row.status !== "Duplicate" && row.documentId != null;
  return {
    suggestionId: row.suggestionId,
    documentId: row.documentId,
    fileName: row.fileName,
    status: row.status,
    errorMessage: row.errorMessage ?? null,
    confidence: row.confidence ?? null,
    categoryCode: row.suggestedCategoryCode ?? "",
    tagCodes: [...row.suggestedTagCodes],
    title: row.suggestedTitle ?? "",
    description: row.suggestedDescription ?? "",
    confidentiality: row.suggestedConfidentiality,
    registryAbbrev: row.registryAbbrev ?? null,
    documentVersion: row.documentVersion ?? null,
    supersedesCandidateDocumentId: row.supersedesCandidateDocumentId ?? null,
    supersedesCandidateTitle: row.supersedesCandidateTitle ?? null,
    supersedesConfidence: row.supersedesConfidence ?? null,
    archivePredecessor: supersedesRecommended,
    included: committable,
  };
}

// Sort NeedsReview / amber-confidence rows to the top so the admin's eye
// lands on them first.
function sortRows(a: EditableRow, b: EditableRow): number {
  const aNeeds = needsAttention(a) ? 0 : 1;
  const bNeeds = needsAttention(b) ? 0 : 1;
  if (aNeeds !== bNeeds) return aNeeds - bNeeds;
  return a.fileName.localeCompare(b.fileName);
}

function needsAttention(r: EditableRow): boolean {
  if (r.status === "NeedsReview" || r.status === "Failed") return true;
  if (r.confidence != null && r.confidence < HIGH_CONFIDENCE) return true;
  return false;
}

function toCommitRow(r: EditableRow): KbAiIntakeCommitRow {
  return {
    documentId: r.documentId!, // included rows always have a documentId
    categoryCode: r.categoryCode,
    tagCodes: r.tagCodes,
    title: r.title.trim(),
    description: r.description.trim() || null,
    confidentiality: r.confidentiality,
    registryAbbrev: r.registryAbbrev?.trim() || null,
    documentVersion: r.documentVersion?.trim() || null,
    supersedesDocumentId:
      r.archivePredecessor && r.supersedesCandidateDocumentId
        ? r.supersedesCandidateDocumentId
        : null,
  };
}

export function KbAiIntakeDialog({
  categories,
  tags,
  onClose,
  onSaved,
}: KbAiIntakeDialogProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [batch, setBatch] = useState<KbAiIntakeBatch | null>(null);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const intakeMutation = useMutation({
    mutationFn: (toUpload: File[]) => knowledgeBaseApi.aiIntake(toUpload),
    onSuccess: (result) => {
      setBatch(result);
      setRows(result.rows.map(seedRow).sort(sortRows));
      setErrors([]);
    },
    onError: (err) => {
      setErrors(
        err instanceof ApiError && err.errors.length
          ? err.errors
          : [err instanceof Error ? err.message : "Classification failed."],
      );
    },
  });

  const commitMutation = useMutation({
    mutationFn: () => {
      if (!batch) throw new Error("No batch to commit.");
      const payload = rows.filter((r) => r.included).map(toCommitRow);
      return knowledgeBaseApi.commitAiIntake(batch.batchId, payload);
    },
    onSuccess: (result) => {
      const parts = [`${result.committedCount} committed`];
      if (result.rejectedCount > 0) parts.push(`${result.rejectedCount} rejected`);
      if (result.failedCount > 0) parts.push(`${result.failedCount} failed`);
      if (result.supersededCount > 0)
        parts.push(`${result.supersededCount} predecessor(s) archived`);
      if (result.failedCount > 0) {
        toast.error(parts.join(" · "));
      } else {
        toast.success(parts.join(" · "));
      }
      onSaved();
    },
    onError: (err) => {
      setErrors(
        err instanceof ApiError && err.errors.length
          ? err.errors
          : [err instanceof Error ? err.message : "Commit failed."],
      );
    },
  });

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const next = Array.from(list);
    if (next.length > MAX_BATCH) {
      toast.error(`At most ${MAX_BATCH} files per batch; you selected ${next.length}.`);
      return;
    }
    setFiles(next);
  };

  const submitIntake = () => {
    if (files.length === 0) {
      setErrors(["Select at least one file."]);
      return;
    }
    setErrors([]);
    intakeMutation.mutate(files);
  };

  const submitCommit = () => {
    setErrors([]);
    const included = rows.filter((r) => r.included);
    const issues: string[] = [];
    for (const r of included) {
      if (!r.title.trim()) issues.push(`${r.fileName}: title is required.`);
      if (!r.categoryCode) issues.push(`${r.fileName}: category is required.`);
    }
    if (included.length === 0) {
      issues.push("Keep at least one row to commit, or close the dialog.");
    }
    if (issues.length) {
      setErrors(issues);
      return;
    }
    commitMutation.mutate();
  };

  const updateRow = (suggestionId: string, patch: Partial<EditableRow>) => {
    setRows((prev) =>
      prev.map((r) => (r.suggestionId === suggestionId ? { ...r, ...patch } : r)),
    );
  };

  const inReview = batch !== null;
  const includedCount = useMemo(() => rows.filter((r) => r.included).length, [rows]);

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={inReview ? `AI review · ${rows.length} document(s)` : "AI-assisted upload"}
      description={
        inReview
          ? "Edit each row's category, tags, title, and description, then commit. Excluded rows are deleted server-side."
          : `Drop up to ${MAX_BATCH} files. Claude will classify each one; you'll review and commit on the next screen.`
      }
      size="xl"
      footer={
        inReview ? (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={commitMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitCommit}
              loading={commitMutation.isPending}
              disabled={includedCount === 0}
            >
              Commit {includedCount} document{includedCount === 1 ? "" : "s"}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={intakeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitIntake}
              loading={intakeMutation.isPending}
              disabled={files.length === 0}
            >
              <Sparkles size={16} aria-hidden /> Classify{" "}
              {files.length > 0 ? `${files.length} ` : ""}
              document{files.length === 1 ? "" : "s"}
            </Button>
          </>
        )
      }
    >
      {inReview ? (
        <ReviewGrid
          rows={rows}
          categories={categories}
          tags={tags}
          updateRow={updateRow}
          errors={errors}
          isPending={commitMutation.isPending}
        />
      ) : (
        <PhaseA
          files={files}
          dragOver={dragOver}
          setDragOver={setDragOver}
          handleFiles={handleFiles}
          fileInputRef={fileInputRef}
          errors={errors}
          isPending={intakeMutation.isPending}
        />
      )}
    </Modal>
  );
}

interface PhaseAProps {
  files: File[];
  dragOver: boolean;
  setDragOver: (b: boolean) => void;
  handleFiles: (list: FileList | null) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  errors: string[];
  isPending: boolean;
}

function PhaseA({
  files,
  dragOver,
  setDragOver,
  handleFiles,
  fileInputRef,
  errors,
  isPending,
}: PhaseAProps) {
  return (
    <div className="space-y-space-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={
          "border-2 border-dashed rounded-md p-space-4 text-center transition-colors " +
          (dragOver ? "border-gold bg-gold/5" : "border-border-light bg-white-warm")
        }
      >
        <UploadCloud size={28} className="mx-auto text-gold-shadow" aria-hidden />
        <p className="text-small text-onyx mt-space-2">
          Drag files here, or{" "}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="font-display font-semibold text-gold underline-offset-4 hover:underline"
          >
            pick files
          </button>
          .
        </p>
        <p className="text-caption text-gray-mid mt-space-1">
          Up to {MAX_BATCH} files per batch. Claude will suggest a category, tags,
          title, description, and supersession candidate per file.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="sr-only"
          aria-label="Choose files"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {files.length > 0 && (
        <ul className="border border-border-light rounded-md divide-y divide-border-light max-h-64 overflow-auto">
          {files.map((f, idx) => (
            <li
              key={`${f.name}-${idx}`}
              className="flex items-center gap-space-2 px-space-3 py-space-2 text-small"
            >
              <FileText size={14} className="text-gray-mid" aria-hidden />
              <span className="flex-1 truncate" title={f.name}>
                {f.name}
              </span>
              <span className="text-caption text-gray-mid">{formatBytes(f.size)}</span>
            </li>
          ))}
        </ul>
      )}

      {isPending && (
        <div
          role="status"
          className="rounded-md border border-border-light bg-white-warm p-space-3 text-small text-onyx"
        >
          Reading and classifying {files.length} document{files.length === 1 ? "" : "s"}…
          this can take up to a minute for large batches.
        </div>
      )}

      {errors.length > 0 && (
        <ul className="text-caption text-status-error space-y-px" role="alert">
          {errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface ReviewGridProps {
  rows: EditableRow[];
  categories: KbCategory[];
  tags: KbTag[];
  updateRow: (suggestionId: string, patch: Partial<EditableRow>) => void;
  errors: string[];
  isPending: boolean;
}

function ReviewGrid({
  rows,
  categories,
  tags,
  updateRow,
  errors,
  isPending,
}: ReviewGridProps) {
  const tagsByKind = useMemo(() => groupBy(tags, (t) => t.kind), [tags]);

  return (
    <div className="space-y-space-4">
      {errors.length > 0 && (
        <ul
          className="rounded-md border border-status-error/40 bg-status-error/5 p-space-3 text-caption text-status-error space-y-px"
          role="alert"
        >
          {errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}

      <ul className="divide-y divide-border-light border border-border-light rounded-md">
        {rows.map((row) => (
          <li key={row.suggestionId} className="p-space-3 space-y-space-3">
            <RowHeader
              row={row}
              onIncludeChange={(v) => updateRow(row.suggestionId, { included: v })}
              disabled={isPending}
            />
            {row.included && (
              <RowEditor
                row={row}
                categories={categories}
                tagsByKind={tagsByKind}
                onChange={(patch) => updateRow(row.suggestionId, patch)}
                disabled={isPending}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface RowHeaderProps {
  row: EditableRow;
  onIncludeChange: (v: boolean) => void;
  disabled: boolean;
}

function RowHeader({ row, onIncludeChange, disabled }: RowHeaderProps) {
  const commitable = row.documentId != null && row.status !== "Failed";
  return (
    <div className="flex items-start gap-space-3">
      <label className="flex items-center gap-space-2 pt-space-1 text-small text-onyx whitespace-nowrap">
        <input
          type="checkbox"
          checked={row.included}
          onChange={(e) => onIncludeChange(e.target.checked)}
          disabled={disabled || !commitable}
          aria-label={`Include ${row.fileName}`}
        />
        Include
      </label>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-space-2">
          <FileText size={14} className="text-gray-mid shrink-0" aria-hidden />
          <span className="font-semibold text-onyx truncate" title={row.fileName}>
            {row.fileName}
          </span>
          <span
            className={
              "inline-flex shrink-0 px-space-2 py-px rounded text-caption font-display font-semibold " +
              statusClass(row.status)
            }
          >
            {statusLabel(row.status)}
          </span>
          {row.confidence != null && (
            <ConfidenceBadge value={row.confidence} status={row.status} />
          )}
        </div>
        {row.errorMessage && (
          <p className="text-caption text-status-error mt-space-1">{row.errorMessage}</p>
        )}
      </div>
    </div>
  );
}

interface RowEditorProps {
  row: EditableRow;
  categories: KbCategory[];
  tagsByKind: Map<string, KbTag[]>;
  onChange: (patch: Partial<EditableRow>) => void;
  disabled: boolean;
}

function RowEditor({ row, categories, tagsByKind, onChange, disabled }: RowEditorProps) {
  const toggleTag = (code: string) => {
    if (row.tagCodes.includes(code)) {
      onChange({ tagCodes: row.tagCodes.filter((c) => c !== code) });
    } else {
      onChange({ tagCodes: [...row.tagCodes, code] });
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 pl-space-5">
      <FormField label="Title" required htmlFor={`ai-title-${row.suggestionId}`}>
        <Input
          id={`ai-title-${row.suggestionId}`}
          value={row.title}
          onChange={(e) => onChange({ title: e.target.value })}
          maxLength={200}
          disabled={disabled}
        />
      </FormField>
      <FormField label="Category" required htmlFor={`ai-cat-${row.suggestionId}`}>
        <Select
          id={`ai-cat-${row.suggestionId}`}
          value={row.categoryCode}
          onChange={(e) => onChange({ categoryCode: e.target.value })}
          disabled={disabled}
        >
          <option value="">Choose a category…</option>
          {categories.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField
        label="Description"
        htmlFor={`ai-desc-${row.suggestionId}`}
        className="md:col-span-2"
      >
        <Textarea
          id={`ai-desc-${row.suggestionId}`}
          value={row.description}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={2}
          maxLength={1000}
          disabled={disabled}
        />
      </FormField>

      <FormField
        label="Confidentiality"
        required
        htmlFor={`ai-conf-${row.suggestionId}`}
      >
        <Select
          id={`ai-conf-${row.suggestionId}`}
          value={row.confidentiality}
          onChange={(e) =>
            onChange({ confidentiality: e.target.value as KbConfidentiality })
          }
          disabled={disabled}
        >
          <option value="InternalOnly">Internal only</option>
          <option value="CounterpartyFacing">Counterparty-facing</option>
        </Select>
      </FormField>

      <FormField label="Tags" htmlFor={`ai-tags-${row.suggestionId}`}>
        <div className="flex flex-wrap gap-space-1" id={`ai-tags-${row.suggestionId}`}>
          {tagsByKind.size === 0 ? (
            <span className="text-caption text-gray-mid italic">No tags defined yet.</span>
          ) : (
            Array.from(tagsByKind.entries()).map(([kind, list]) => (
              <div key={kind} className="flex flex-wrap items-center gap-space-1">
                <span className="text-caption uppercase tracking-wide text-gray-mid mr-space-1">
                  {kind}
                </span>
                {list.map((t) => {
                  const selected = row.tagCodes.includes(t.code);
                  return (
                    <button
                      key={t.code}
                      type="button"
                      onClick={() => toggleTag(t.code)}
                      aria-pressed={selected}
                      disabled={disabled}
                      className={
                        selected
                          ? "px-space-2 py-[2px] rounded-full text-caption font-display font-semibold border bg-gold/15 border-gold text-gold-shadow"
                          : "px-space-2 py-[2px] rounded-full text-caption font-display font-semibold border bg-white-pure border-border-light text-gray-mid hover:border-gold-shadow"
                      }
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </FormField>

      {row.supersedesCandidateDocumentId && (
        <div className="md:col-span-2 rounded-md border border-border-light bg-white-warm p-space-3 space-y-space-2">
          <label className="flex items-start gap-space-2 text-small text-onyx">
            <input
              type="checkbox"
              className="mt-1"
              checked={row.archivePredecessor}
              onChange={(e) => onChange({ archivePredecessor: e.target.checked })}
              disabled={disabled}
            />
            <span>
              Supersedes{" "}
              <strong>{row.supersedesCandidateTitle ?? "an existing document"}</strong>
              {" — predecessor will be archived (stays accessible)."}
              {row.supersedesConfidence != null && (
                <span className="text-caption text-gray-mid">
                  {" "}
                  · confidence {formatPercent(row.supersedesConfidence)}
                </span>
              )}
            </span>
          </label>
          <SupersedesPicker
            value={row.supersedesCandidateDocumentId}
            onChange={(id, title) =>
              onChange({
                supersedesCandidateDocumentId: id,
                supersedesCandidateTitle: title,
              })
            }
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

interface SupersedesPickerProps {
  value: string | null;
  onChange: (id: string | null, title: string | null) => void;
  disabled: boolean;
}

function SupersedesPicker({ value, onChange, disabled }: SupersedesPickerProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<KbDocument[] | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (options !== null) return;
    setLoading(true);
    try {
      const docs = await knowledgeBaseApi.listDocuments({ includeRetired: false });
      setOptions(docs);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pl-space-5">
      {!open ? (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            void load();
          }}
          disabled={disabled}
          className="text-caption text-gold-shadow underline-offset-4 hover:underline"
        >
          Change predecessor
        </button>
      ) : (
        <div className="flex items-center gap-space-2">
          <Select
            aria-label="Choose predecessor document"
            value={value ?? ""}
            disabled={disabled || loading}
            onChange={(e) => {
              const id = e.target.value || null;
              const title = id
                ? (options?.find((d) => d.id === id)?.title ?? null)
                : null;
              onChange(id, title);
            }}
          >
            <option value="">— none —</option>
            {(options ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.title}
              </option>
            ))}
          </Select>
          <button
            type="button"
            onClick={() => setOpen(false)}
            disabled={disabled}
            className="text-caption text-gray-mid hover:underline"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}

function ConfidenceBadge({
  value,
  status,
}: {
  value: number;
  status: KbAiIntakeStatus;
}) {
  const isHigh = value >= HIGH_CONFIDENCE && status !== "NeedsReview";
  return (
    <span
      className={
        "inline-flex shrink-0 px-space-2 py-px rounded text-caption font-display font-semibold " +
        (isHigh
          ? "bg-status-success/15 text-status-success"
          : "bg-status-warning/15 text-status-warning")
      }
      title={isHigh ? "High confidence" : "Needs review"}
    >
      {isHigh ? "Confident" : "Review"} · {formatPercent(value)}
    </span>
  );
}

function statusLabel(s: KbAiIntakeStatus): string {
  switch (s) {
    case "Suggested":
      return "Suggested";
    case "NeedsReview":
      return "Needs review";
    case "Duplicate":
      return "Duplicate";
    case "Failed":
      return "Failed";
  }
}

function statusClass(s: KbAiIntakeStatus): string {
  switch (s) {
    case "Suggested":
      return "bg-status-success/15 text-status-success";
    case "NeedsReview":
      return "bg-status-warning/15 text-status-warning";
    case "Duplicate":
      return "bg-gold/15 text-gold-shadow";
    case "Failed":
      return "bg-status-error/15 text-status-error";
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatPercent(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function groupBy<T, K>(items: T[], key: (t: T) => K): Map<K, T[]> {
  const out = new Map<K, T[]>();
  for (const item of items) {
    const k = key(item);
    const arr = out.get(k);
    if (arr) arr.push(item);
    else out.set(k, [item]);
  }
  return out;
}
